import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

type TaCommsRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   roomNumber: string;
   name: string;
   confirmationNumber: string;
   reservationId: string;
   transactionCode: string;
   transactionDescription: string;
   folioWindowNo: string;
   amount: string;
   amountRaw: number;
   cashId: string;
   hasRealTyallsRowId: boolean;
};

const TRANSACTION_CODE_DESCRIPTIONS: Record<string, string> = {
   '9400': 'T/A Prepaid commission',
   '9405': 'T/A Prepaid Commission Fore'
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function getPathValue(source: any, path: string): any {
   if (!source || !path) return undefined;

   return path.split('.').reduce((current, part) => current?.[part], source);
}

function getFirstValue(source: any, paths: string[]): any {
   for (const path of paths) {
      const value = getPathValue(source, path);

      if (value !== null && value !== undefined && value !== '') {
         return value;
      }
   }

   return undefined;
}

function getFirstString(source: any, paths: string[]): string {
   return asString(getFirstValue(source, paths));
}

function toNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/^-\s*/, '-');

   const amount = Number(cleaned);
   return Number.isFinite(amount) ? amount : null;
}

function getFirstNumber(source: any, paths: string[]): number | null {
   for (const path of paths) {
      const amount = toNumberOrNull(getPathValue(source, path));

      if (amount !== null) return amount;
   }

   return null;
}

function formatCurrency(value: number): string {
   const formatted = Math.abs(value).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
   if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(
         2,
         '0'
      )}/${slashMatch[3]}`;
   }

   return raw;
}

function formatTime(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoTimeMatch = raw.match(/T(\d{2}:\d{2})/);
   if (isoTimeMatch) return isoTimeMatch[1];

   const simpleTimeMatch = raw.match(/^(\d{1,2}):(\d{2})/);
   if (simpleTimeMatch) {
      return `${simpleTimeMatch[1].padStart(2, '0')}:${simpleTimeMatch[2]}`;
   }

   return raw;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      getFirstString(row, ['Id', 'id', 'ReservationId', 'reservationId']) ||
      `ta-comms:${getFirstString(row, ['Date', 'date'])}:${getFirstString(row, [
         'RoomNumber',
         'roomNumber'
      ])}:${getFirstString(row, [
         'ConfirmationNumber',
         'confirmationNumber'
      ])}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function getSourceRows(reportJson: any): any[] {
   const parsed = parseReportJson(reportJson);

   if (Array.isArray(parsed?.TaComms)) {
      return parsed.TaComms;
   }

   if (Array.isArray(parsed?.TAComms)) {
      return parsed.TAComms;
   }

   if (Array.isArray(parsed?.Rows)) {
      return parsed.Rows;
   }

   if (Array.isArray(parsed?.rows)) {
      return parsed.rows;
   }

   return [];
}

function getTransactionCode(row: any): string {
   return getFirstString(row, [
      'TransactionCode',
      'transactionCode',
      'TrnCode',
      'trnCode'
   ]);
}

function getTransactionDescription(row: any): string {
   const transactionCode = getTransactionCode(row);

   return (
      getFirstString(row, [
         'TransactionCodeDescription',
         'transactionCodeDescription',
         'TransactionDescription',
         'transactionDescription',
         'Description',
         'description'
      ]) ||
      TRANSACTION_CODE_DESCRIPTIONS[transactionCode] ||
      transactionCode
   );
}

/**
 * Saved TA Comms shape:
 * {
 *   Debit: 0,
 *   Credit: 341.06
 * }
 *
 * Main amount is normally the Credit value.
 */
function getAmount(row: any): number {
   const credit = getFirstNumber(row, ['Credit', 'credit']);
   if (credit !== null) return credit;

   const debit = getFirstNumber(row, ['Debit', 'debit']);
   if (debit !== null) return debit;

   const amount = getFirstNumber(row, ['Amount', 'amount', 'AmountRaw', 'amountRaw']);
   return amount ?? 0;
}

function normaliseRows(reportJson: any): TaCommsRow[] {
   return getSourceRows(reportJson)
      .map((row) => {
         const dateRaw = getFirstString(row, ['Date', 'date']);
         const amountRaw = getAmount(row);

         return {
            rowId: getRowId(row),
            date: formatDate(dateRaw),
            dateRaw,
            time: formatTime(getFirstString(row, ['Time', 'time'])),
            roomNumber: getFirstString(row, ['RoomNumber', 'roomNumber']),
            name: getFirstString(row, ['Name', 'name', 'GuestName', 'guestName']),
            confirmationNumber: getFirstString(row, [
               'ConfirmationNumber',
               'confirmationNumber'
            ]),
            reservationId: getFirstString(row, ['ReservationId', 'reservationId']),
            transactionCode: getTransactionCode(row),
            transactionDescription: getTransactionDescription(row),
            folioWindowNo: getFirstString(row, ['FolioWindowNo', 'folioWindowNo']),
            amount: formatCurrency(amountRaw),
            amountRaw,
            cashId: getFirstString(row, ['CashId', 'cashId']),
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter(
         (row) =>
            row.roomNumber ||
            row.name ||
            row.confirmationNumber ||
            row.amountRaw !== 0
      );
}

function sortRows(rows: TaCommsRow[]): TaCommsRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);
      if (dateCompare !== 0) return dateCompare;

      const aRoom = Number(a.roomNumber);
      const bRoom = Number(b.roomNumber);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

export const TA_COMMS_DEFINITION: ReportDefinition = {
   reportType: 'TaComms',
   version: 2,
   title: 'TA Comms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'TA Comms',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'taComms'
         );

         const totalAmount = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['amountRaw']);
            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length
            },
            {
               label: 'Total Comms',
               value: formatCurrency(totalAmount),
               tone: totalAmount < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['taComms']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['taComms'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'roomNumber',
         header: 'ROOM',
         width: '90px',
         sort: { type: 'string' }
      },
      {
         key: 'name',
         header: 'GUEST NAME',
         width: '190px',
         maxChars: 80,
         sort: { type: 'string' }
      },
      {
         key: 'confirmationNumber',
         header: 'CONFIRMATION',
         width: '150px',
         sort: { type: 'string' }
      },
      {
         key: 'transactionCode',
         header: 'TRN CODE',
         width: '100px',
         sort: { type: 'string' }
      },
      {
         key: 'transactionDescription',
         header: 'DESCRIPTION',
         width: '220px',
         maxChars: 90,
         sort: { type: 'string' }
      },
      {
         key: 'folioWindowNo',
         header: 'FOLIO',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'cashId',
         header: 'CASH ID',
         width: '120px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'roomNumber', label: 'Room', type: 'string' },
         { key: 'name', label: 'Guest Name', type: 'string' },
         {
            key: 'confirmationNumber',
            label: 'Confirmation Number',
            type: 'string'
         },
         { key: 'transactionCode', label: 'Transaction Code', type: 'string' },
         {
            key: 'transactionDescription',
            label: 'Description',
            type: 'string'
         },
         { key: 'folioWindowNo', label: 'Folio Window', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'cashId', label: 'Cash ID', type: 'string' }
      ]
   },

   flattenRows: (reportJson: any): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'taComms',
            date: row.date,
            dateRaw: row.dateRaw,
            time: row.time,
            roomNumber: row.roomNumber,
            name: row.name,
            confirmationNumber: row.confirmationNumber,
            reservationId: row.reservationId,
            transactionCode: row.transactionCode,
            transactionDescription: row.transactionDescription,
            folioWindowNo: row.folioWindowNo,
            amount: row.amount,
            amountRaw: row.amountRaw,
            cashId: row.cashId
         }
      }));
   }
};
